import { Add, Close, Edit } from '@mui/icons-material';
import { Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { Badge, ListGroup, Stack } from 'react-bootstrap';
import { useNotification } from '../../hooks/NotificationContext';
import { isExecutiveManager } from '../../lib/userUtils';
import { getWeblinks, removeWeblink } from '../../modules/traceability';
import DetailsCard from '../../ui/layout/DetailsCard';
import CustomIconButton from '../../components/elements/CustomIconButton';
import SkeletonLoader from '../../components/elements/SkeletonLoader';
import ConfirmationModal from '../../ui/primitives/ConfirmationModel';
import WebLinksModal from '../../components/elements/common/traceability/WebLinkModal';

const WebLinks = ({ entityType, entityId }) => {
  const [weblinks, setWeblinks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [selectedWebLink, setSelectedWebLink] = useState(null);
  const { addNotification } = useNotification();

  const fetchAllWebLinks = async () => {
    setLoading(true);
    try {
      const response = await getWeblinks(entityType, entityId);
      if (response) {
        setWeblinks(response?.data);
        setError(null);
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const deleteWeblink = async () => {
    try {
      await removeWeblink(selectedWebLink.id);
      fetchAllWebLinks();
      addNotification('success', 'Link deleted successfully.');
    } catch (error) {
      const errorMessage =
        'Failed to remove link. Please refresh once and try again!';
      addNotification('danger', errorMessage);
    } finally {
      handleConfirmClose();
    }
  };

  const handleEditLink = (link) => {
    setShowModal(true);
    setSelectedWebLink(link);
  };

  const handleShowConfirm = (link) => {
    setShowConfirm(true);
    setSelectedWebLink(link);
  };

  const handleConfirmClose = () => {
    setShowConfirm(false);
    setSelectedWebLink(null);
  };

  useEffect(() => {
    if (entityId) {
      fetchAllWebLinks();
    }
    return () => {
      setWeblinks([]);
    };
  }, [entityId, entityType]);

  const openNewWindow = (url) => {
    const hasProtocol = /^https?:\/\//i;

    // If the URL doesn't start with a protocol, prepend 'http://'
    if (!hasProtocol.test(url)) {
      url = 'http://' + url; // or 'https://' if you prefer
    }
    window.open(url, '_blank');
  };

  const cardTitle = (
    <Stack direction="row" alignItems="center" spacing={1.25}>
      <Typography variant="h6">Web Links</Typography>
    </Stack>
  );

  const cardActions = !isExecutiveManager() && (
    <CustomIconButton
      disableRipple
      title='Add Web Link'
      onClick={() => {
        setShowModal(true);
      }}>
      <Add color='primary' />
    </CustomIconButton>
  );

  return (
    <>
      <DetailsCard title={cardTitle} actions={cardActions}>
        {error && <p className="text-danger">{error}</p>}
        {loading ? (
          <SkeletonLoader rowCount={2} />
        ) : weblinks?.length === 0 ? (
          <p className='text-center text-muted'>No web links</p>
        ) : (
          <ListGroup as="ol" numbered>
            {weblinks?.map((link, index) => (
              <ListGroup.Item
                as="li"
                action
                style={{ cursor: 'pointer' }}
                key={link.url + index}
                className="d-flex justify-content-between align-items-start">
                <div className="ms-2 me-auto">
                  <div className="fw-bold">
                    <p
                      className="text-primary"
                      onClick={() => openNewWindow(link.url)}>
                      {link.url}
                    </p>
                  </div>
                  <p>{link.description}</p>
                </div>
                <Stack direction="horizontal" gap={2}>
                  <Badge
                    bg="info"
                    pill
                    title="Edit weblink"
                    onClick={() => handleEditLink(link)}>
                    <Edit fontSize="sm" />
                  </Badge>
                  <Badge
                    bg="danger"
                    pill
                    className="ml-2"
                    title="Delete weblink"
                    onClick={() => handleShowConfirm(link)}>
                    <Close fontSize="sm" />
                  </Badge>
                </Stack>
              </ListGroup.Item>
            ))}
          </ListGroup>
        )}
      </DetailsCard>

      <WebLinksModal
        show={showModal}
        handleClose={() => {
          setShowModal(false);
          setSelectedWebLink(null);
        }}
        entityId={entityId}
        entityType={entityType}
        fetchAllWebLinks={fetchAllWebLinks}
        selectedWebLink={selectedWebLink}
        addNotification={addNotification}
      />
      <ConfirmationModal
        show={showConfirm}
        handleConfirm={deleteWeblink}
        handleClose={handleConfirmClose}
        title="Remove Web Link"
        body={'Are you sure you want to remove this weblink?'}
      />
    </>
  );
};

export default WebLinks;
